/**
 * =====================================================================
 * IMPORTANT - READ BEFORE MODIFYING ANY LIST ENDPOINT IN THIS FILE:
 * =====================================================================
 *
 * TNN's Django backend paginates ALL list endpoints by default:
 *
 *   API returns: { "count": N, "results": [...actual data...] }
 *   baseApi unwraps to: { "count": N, "results": [...] }  (NOT an array)
 *
 * Every ``build.query<Type[], ...>`` endpoint MUST have:
 *
 *   transformResponse: (r: { results?: Type[] }) =>
 *     Array.isArray(r?.results) ? r.results : [],
 *
 * Without this, ``.map()`` on the paginated object either crashes
 * ("TypeError: x.map is not a function") or silently shows nothing.
 * This was the #1 bug class in the v1.6 and v4.0.1 QA reports.
 * =====================================================================
 */
/**
 * Org-member invitation API.
 *
 * Mirrors backend/user/invitation_views.py at /api/v1/auth/invitations/.
 * The list/create/revoke endpoints need an authenticated Owner/Admin;
 * preview/accept are public (the invitee has no account yet).
 */
import { baseApi } from "@/app/store/api/baseApi";

import type { LoginResponse } from "@/features/auth/api/authApi";

export type InvitationStatus = "pending" | "accepted" | "revoked" | "expired";

export interface Invitation {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  role_display: string;
  status: InvitationStatus;
  status_display: string;
  invited_by_email: string;
  is_expired: boolean;
  created_at: string;
  expires_at: string;
  accepted_at: string | null;
}

export interface InvitationCreateResponse extends Invitation {
  accept_url: string;
}

export interface InvitationPreview {
  valid: boolean;
  reason?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  organization_name?: string;
  role?: string;
  role_display?: string;
}

export interface TeamMember {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  role_display: string;
  date_joined: string | null;
  is_active?: boolean;
}

export interface ListTeamMembersParams {
  search?: string;
}

/** Backend may paginate with `results` or return a `members` envelope. */
export const TEAM_MEMBERS_PAGE_SIZE = 20;

type TeamMembersPagePayload = {
  items: TeamMember[];
  count: number;
};

function parseTeamMembersPage(response: unknown): TeamMembersPagePayload {
  if (Array.isArray(response)) {
    return { items: response, count: response.length };
  }
  if (!response || typeof response !== "object") {
    return { items: [], count: 0 };
  }
  const body = response as {
    members?: TeamMember[];
    results?: TeamMember[];
    count?: number;
  };
  const items = Array.isArray(body.members)
    ? body.members
    : Array.isArray(body.results)
      ? body.results
      : [];
  const count = typeof body.count === "number" ? body.count : items.length;
  return { items, count };
}

/** RTK Query endpoints for invitations (create, accept). */
export const invitationApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listTeamMembers: build.query<TeamMember[], ListTeamMembersParams | void>({
      async queryFn(params, _api, _extraOptions, baseQuery) {
        const search = params?.search?.trim();
        const requestParams: Record<string, string | number> = {
          page: 1,
          page_size: TEAM_MEMBERS_PAGE_SIZE,
        };
        if (search) requestParams.search = search;

        const collected: TeamMember[] = [];
        let page = 1;
        let total = 0;

        for (let guard = 0; guard < 50; guard += 1) {
          const result = await baseQuery({
            url: "/auth/members/",
            params: { ...requestParams, page },
          });
          if (result.error) {
            return { error: result.error };
          }

          const { items, count } = parseTeamMembersPage(result.data);
          total = count;
          collected.push(...items);

          if (collected.length >= total || items.length < TEAM_MEMBERS_PAGE_SIZE) {
            break;
          }
          page += 1;
        }

        const byId = new Map<number, TeamMember>();
        for (const member of collected) {
          byId.set(member.id, member);
        }
        return { data: [...byId.values()] };
      },
      providesTags: [{ type: "User", id: "TEAM_MEMBERS" }],
    }),
    listInvitations: build.query<Invitation[], void>({
      query: () => ({ url: "/auth/invitations/" }),
      transformResponse: (r: Invitation[] | { results?: Invitation[] }) =>
        Array.isArray(r) ? r : Array.isArray(r?.results) ? r.results : [],
      providesTags: [{ type: "Invitation", id: "LIST" }],
    }),
    createInvitation: build.mutation<
      InvitationCreateResponse,
      { email: string; role: string; first_name?: string; last_name?: string }
    >({
      query: (body) => ({
        url: "/auth/invitations/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Invitation", id: "LIST" }],
    }),
    revokeInvitation: build.mutation<Invitation, number>({
      query: (id) => ({
        url: `/auth/invitations/${id}/revoke/`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "Invitation", id: "LIST" }],
    }),
    resendInvitation: build.mutation<InvitationCreateResponse, number>({
      query: (id) => ({
        url: `/auth/invitations/${id}/resend/`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "Invitation", id: "LIST" }],
    }),
    previewInvitation: build.query<InvitationPreview, string>({
      query: (token) => ({
        url: "/auth/invitations/preview/",
        params: { token },
      }),
    }),
    acceptInvitation: build.mutation<
      LoginResponse,
      {
        token: string;
        password: string;
      }
    >({
      query: (body) => ({
        url: "/auth/invitations/accept/",
        method: "POST",
        body,
      }),
    }),
    updateTeamMember: build.mutation<
      { id: number; is_active: boolean },
      { userId: number; is_active: boolean }
    >({
      query: ({ userId, is_active }) => ({
        url: `/auth/members/${userId}/`,
        method: "PATCH",
        body: { is_active },
      }),
      invalidatesTags: [{ type: "User", id: "TEAM_MEMBERS" }],
    }),
    deleteTeamMember: build.mutation<void, number>({
      query: (userId) => ({
        url: `/auth/members/${userId}/`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "User", id: "TEAM_MEMBERS" }],
    }),
    sendTeamMemberPasswordReset: build.mutation<{ detail: string }, number>({
      query: (userId) => ({
        url: `/auth/members/${userId}/send-password-reset/`,
        method: "POST",
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useListTeamMembersQuery,
  useListInvitationsQuery,
  useCreateInvitationMutation,
  useRevokeInvitationMutation,
  useResendInvitationMutation,
  usePreviewInvitationQuery,
  useAcceptInvitationMutation,
  useUpdateTeamMemberMutation,
  useDeleteTeamMemberMutation,
  useSendTeamMemberPasswordResetMutation,
} = invitationApi;
